export class VideoController {
	constructor({ src, videoId, visibleId, fps = 30 }) {
		this.src = src
		this.videoId = videoId
		this.visibleId = visibleId
		this.fps = fps
		this.frame = 1 / fps

		this.video = null
		this.isReady = false

		this.markers = []
		this.loop = { start: null, end: null, enabled: false }
		this.sectionLoop = { start: null, end: null, customEnd: null, enabled: false }
		this.abLoop = { start: null, end: null, enabled: false }
		this.skips = [] // [{from: number, to: number}]
		this.skipsEnabled = false
		this.onStateChange = null
		this._playRequested = false
		this.playbackRate = 1 // 追加
		this.countMode = false // 追加

		this.thumbnailUrl = this.videoId ? `https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg` : ''
		
		this.clickAudio = new Audio('/assets/click.mp3')
		this.clickAudio1 = new Audio('/assets/click1.mp3')
		this.clickAudio.volume = 0.5
		this.clickAudio1.volume = 1.0
		this._countdownTimer = null

		this._init()
	}

	_init() {
		const container = document.getElementById(this.visibleId)
		if (!container) return

		this.video = document.createElement('video')
		this.video.src = this.src
		this.video.controls = false
		this.video.style.width = '100%'
		this.video.style.height = '100%'
		this.video.playsInline = true
		container.appendChild(this.video)

		this.video.addEventListener('loadedmetadata', () => {
			this.isReady = true
			this._startUpdateLoop()
		})

		this.video.addEventListener('play', () => {
			if (this.onStateChange) this.onStateChange(1) // 1 = PLAYING (YT.PlayerState.PLAYING)
		})

		this.video.addEventListener('pause', () => {
			if (this.onStateChange) this.onStateChange(2) // 2 = PAUSED (YT.PlayerState.PAUSED)
		})

		this.video.addEventListener('ended', () => {
			if (this.onStateChange) this.onStateChange(0) // 0 = ENDED (YT.PlayerState.ENDED)
		})
	}

	/* ======================
		 基本操作
	====================== */

	play() {
		this._playRequested = true
		if (!this.isReady) {
			this.video.addEventListener('loadedmetadata', () => {
				if (this._playRequested) {
					this.play()
				}
			}, { once: true })
			return
		}

		const introMarker = this.markers.find(m => m.label === 'INTRO')

		// カウントモードが有効な場合、必ずイントロからスタートする
		if (this.countMode) {
			const introTime = introMarker ? introMarker.time : 0
			this.seek(introTime)

			// 等倍速の場合のみカウントダウンを行う
			if (this.playbackRate === 1) {
				this._startCountdown(() => {
					if (this._playRequested) {
						this.video.play()
					}
				})
				return
			}
		}

		this.video.play()
	}

	_startCountdown(callback) {
		this.stopCountdown()

		const bpm = 176
		const interval = (60 / bpm) * 1000
		let count = 0
		const totalClicks = 8

		const click = () => {
			if (!this._playRequested) return

			// 7拍目（count === 6）と8拍目（count === 7）は音を鳴らさない
			if (count < 6) {
				// 1拍目と5拍目をclick1に (countは0から始まるので、0と4が該当)
				if (count === 0 || count === 4) {
					this.clickAudio1.currentTime = 0
					this.clickAudio1.play().catch(e => console.error('Click audio1 play failed:', e))
				} else {
					this.clickAudio.currentTime = 0
					this.clickAudio.play().catch(e => console.error('Click audio play failed:', e))
				}
			}
			
			count++

			if (count < totalClicks) {
				this._countdownTimer = setTimeout(click, interval)
			} else {
				this._countdownTimer = null
				callback()
			}
		}

		this._countdownTimer = setTimeout(click, interval)
	}

	stopCountdown() {
		if (this._countdownTimer) {
			clearTimeout(this._countdownTimer)
			this._countdownTimer = null
		}
	}

	pause() {
		this._playRequested = false
		this.stopCountdown()
		this.video.pause()
	}

	togglePlay() {
		if (this.video.paused) {
			this.play()
		} else {
			this.pause()
		}
	}

	seek(time) {
		if (this.video.readyState >= 1) {
			this.video.currentTime = time
		} else {
			this.video.addEventListener('loadedmetadata', () => {
				this.video.currentTime = time
			}, { once: true })
			return // まだメタデータがない場合はここで終了
		}

		if (this.sectionLoop.enabled) {
			const t = time
			const start = this.sectionLoop.start
			const end = this.sectionLoop.end
			
			if (this.sectionLoop.customEnd !== null) {
				if (t < start || t >= end - 0.1) {
					this.resetSectionRange()
				}
			} else {
				this._updateSectionRange()
			}
		}
	}

	/* ======================
		 擬似フレーム操作
	====================== */

	nextFrame() {
		this.video.currentTime += this.frame
	}

	prevFrame() {
		this.video.currentTime = Math.max(this.video.currentTime - this.frame, 0)
	}

	/* ======================
		 マーカー
	====================== */

	setMarkers(list) {
		this.markers = list
	}

	nextMarker() {
		const t = this.getCurrentTime()
		const next = this.markers.find(m => m.time > t + 0.1)
		if (next) {
			this.seek(next.time)
		}
	}

	prevMarker() {
		const t = this.getCurrentTime()
		const prev = [...this.markers].reverse().find(m => m.time < t - 0.1)
		if (prev) {
			this.seek(prev.time)
		}
	}

	getCurrentMarker() {
		const t = this.getCurrentTime()
		const current = [...this.markers].reverse().find(m => m.time <= t + 0.1)
		return current || null
	}

	/* ======================
		 ループ・スキップ
	====================== */

	setLoop(start, end) {
		this.loop.start = start
		this.loop.end = end
	}

	toggleLoop(enabled) {
		this.loop.enabled = enabled
	}

	setSkips(list) {
		this.skips = list
	}
	
	toggleSkips(enabled) {
		this.skipsEnabled = enabled
	}

	setPointA() {
		if (!this.isReady) return
		this.abLoop.start = this.video.currentTime
		return this.abLoop.start
	}

	setPointB() {
		if (!this.isReady) return
		this.abLoop.end = this.video.currentTime
		return this.abLoop.end
	}

	toggleABLoop(enabled) {
		this.abLoop.enabled = enabled
	}

	toggleSectionLoop(enabled) {
		this.sectionLoop.enabled = enabled
		if (enabled) {
			this._updateSectionRange()
		}
	}

	setSectionRange(start, end) {
		this.sectionLoop.start = start
		this.sectionLoop.end = end
		this.sectionLoop.customEnd = end
	}

	resetSectionRange() {
		this.sectionLoop.customEnd = null
		this._updateSectionRange()
	}

	_updateSectionRange() {
		const t = this.video.currentTime
		const currentIdx = this.markers.findIndex(m => m.time > t + 0.1) - 1
		
		const startIdx = currentIdx < 0 ? 0 : currentIdx
		this.sectionLoop.start = this.markers[startIdx]?.time || 0
		
		if (this.sectionLoop.customEnd === null) {
			this.sectionLoop.end = this.markers[startIdx + 1]?.time || this.getDuration()
		}
	}

	handleSectionClick(time, isShift) {
		if (isShift) {
			this.sectionLoop.customEnd = time
			this.sectionLoop.end = time
		} else {
			this.seek(time)
			this.resetSectionRange()
		}
	}

	/* ======================
		 内部更新ループ
	====================== */

	_startUpdateLoop() {
		const update = () => {
			if (!this.isReady) {
				this._updateRAF = requestAnimationFrame(update)
				return
			}
			const t = this.video.currentTime

			// スキップ判定
			if (this.skipsEnabled) {
				const skip = this.skips.find(s => t >= s.from && t < s.to)
				if (skip) {
					this.seek(skip.to)
				}
			}

			// ループ判定
			if (this.loop.enabled && this.loop.end && t >= this.loop.end - 0.1) {
				this.seek(this.loop.start)
			}

			// ABループ判定
			if (this.abLoop.enabled && this.abLoop.start !== null && this.abLoop.end !== null) {
				if (t >= this.abLoop.end || t < this.abLoop.start) {
					this.seek(this.abLoop.start)
				}
			}

			// 区間（セクション）ループ判定
			if (this.sectionLoop.enabled && this.sectionLoop.start !== null && this.sectionLoop.end !== null) {
				if (t >= this.sectionLoop.end - 0.1 || t < this.sectionLoop.start) {
					this.seek(this.sectionLoop.start)
				}
			}

			this._updateRAF = requestAnimationFrame(update)
		}

		update()
	}

	stopUpdateLoop() {
		cancelAnimationFrame(this._updateRAF)
	}

	/* ======================
		 再生速度
	====================== */

	setSpeed(rate) {
		this.playbackRate = rate
		this.video.playbackRate = rate
	}

	toggleCountMode(enabled) {
		this.countMode = enabled
	}

	/* ======================
		 ボリューム・ミュート
	====================== */

	setVolume(volume) {
		this.video.volume = volume / 100
	}

	mute() {
		this.video.muted = true
	}

	unMute() {
		this.video.muted = false
	}

	isMuted() {
		return this.video ? this.video.muted : false
	}

	/* ======================
		 反転
	====================== */

	flip(el) {
		el.classList.add('is-flipped')
	}

	resetFlip(el) {
		el.classList.remove('is-flipped')
	}

	/* ======================
		 YouTube APIとの互換用
	====================== */
	getDuration() {
		return this.video ? this.video.duration : 0
	}

	getCurrentTime() {
		return this.video ? this.video.currentTime : 0
	}

	getPlayerState() {
		if (!this.video) return -1 // UNSTARTED
		if (this.video.ended) return 0 // ENDED
		if (this.video.paused) return 2 // PAUSED
		return 1 // PLAYING
	}
}
